import { ChangeEvent, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService from '../../services/authService';
import isEmailValid from '../../utils/isEmailValid';
import useErrors from '../../hooks/useErrors';
import formatCpf from '../../utils/formatCpf';
import formatCnpj from '../../utils/formatCnpj';
import isCpfvalid from '../../utils/isCpfValid';
import isCnpjValid from '../../utils/isCnpjValid';
import { useQuery } from '../../hooks/useQuery';
import hasSequentialNumbers from '../../utils/hasSequentialNumbers';
import useApiCall from '../../hooks/useApiCall';

export default function useUpdateRegister() {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailConfirmation, setEmailConfirmation] = useState('');
  const [cpf, setCpf] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  const navigate = useNavigate();
  const query = useQuery();
  const { apiCall } = useApiCall();

  const userCode = query.get('codUsu');
  const userProfile = query.get('perfilUsu');
  const companyCode = query.get('codEmpresa');
  const {
    setError, removeError, getErrorMessageByFieldName, errors,
  } = useErrors();

  const isReadyToSignup = email
  && cpf
  // && cnpj
  && password
  && passwordConfirmation
  && errors.length === 0;

  function handleEmailChange(event: ChangeEvent<HTMLInputElement>) {
    setEmail(event.target.value);

    if (!isEmailValid(event.target.value) || !event.target.value) {
      setError({ field: 'email', message: 'E-mail inválido!' });
    } else if (event.target.value) {
      removeError('email');
    } if (event.target.value !== emailConfirmation) {
      setError({ field: 'emailConfirmation', message: 'E-mails divergentes' });
    } else if (event.target.value === emailConfirmation) {
      removeError('emailConfirmation');
    }
  }

  function handleEmailConfirmationChange(event: ChangeEvent<HTMLInputElement>) {
    setEmailConfirmation(event.target.value);

    if (event.target.value !== email) {
      setError({ field: 'emailConfirmation', message: 'E-mails divergentes' });
    } else {
      removeError('emailConfirmation');
    }
  }

  function handleCpfChange(event: ChangeEvent<HTMLInputElement>) {
    setCpf(formatCpf(event.target.value));
    if (!event.target.value || !isCpfvalid(formatCpf(event.target.value))) {
      setError({ field: 'cpf', message: 'Informe um cpf válido!' });
    } else {
      removeError('cpf');
    }
  }

  function handleCnpjChange(event: ChangeEvent<HTMLInputElement>) {
    setCnpj(formatCnpj(event.target.value));
    if (!event.target.value || !isCnpjValid(formatCnpj(event.target.value))) {
      setError({ field: 'cnpj', message: 'Informe um CNPJ válido!' });
    } else {
      removeError('cnpj');
    }
  }

  function handlePasswordChange(event: ChangeEvent<HTMLInputElement>) {
    setPassword(event.target.value);

    if (event.target.value.length < 8 || hasSequentialNumbers(event.target.value)) {
      setError({ field: 'password', message: 'Senha deve conter ter 8 caracteres, e não pode haver números sequenciais!' });
    } else if (event.target.value) {
      removeError('password');
    } if (event.target.value !== passwordConfirmation) {
      setError({ field: 'passwordConfirmation', message: 'Senhas divergentes' });
    } else if (event.target.value === passwordConfirmation) {
      removeError('passwordConfirmation');
    }
  }

  function handlePasswordConfirmationChange(event: ChangeEvent<HTMLInputElement>) {
    setPasswordConfirmation(event.target.value);

    if (event.target.value !== password) {
      setError({ field: 'passwordConfirmation', message: 'Senhas divergentes' });
    } else {
      removeError('passwordConfirmation');
    }
  }

  const signup = useCallback(async () => {
    await apiCall({
      apiToCall: authService.signup,
      isValidationToken: true,
      reqBody: JSON.stringify({
        cpf,
        email,
        senha: password,
        codEmpresa: companyCode,
        codUsu: userCode,
        perfilUsu: userProfile,
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Ocorreu um erro ao realizar o seu cadastro. Por favor, tente novamente (${response.msg})`);
          return;
        }
        if (!response.validado) {
          toast.error(`Não foi possível validar suas credenciais. Por favor, tente novamente (${response.msg})`);
          return;
        }
        navigate(`/verification?codUsu=${response.codUsu}&perfilUsu=${response.perfilUsu}&codEmpresa=${response.codEmpresa}`);
      },
      catchMessage: 'Não foi possível realizar seu cadastro. Por favor, tente novamente'
    });
  }, [apiCall, companyCode, cpf, email, navigate, password, userCode, userProfile]);

  return {
    isLoading,
    getErrorMessageByFieldName,
    cpf,
    handleCpfChange,
    cnpj,
    handleCnpjChange,
    password,
    handlePasswordChange,
    passwordConfirmation,
    handlePasswordConfirmationChange,
    email,
    handleEmailChange,
    signup,
    isReadyToSignup,
    emailConfirmation,
    handleEmailConfirmationChange,
  };
}
